import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Printer, Ticket, Trophy, Exam, Bell, ArrowRight, Gift } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fmtDate, fmtDateTime, money, RISK } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, Stat, Empty } from "@/components/layout/DashShell";
import { Loader } from "@/components/Blocks";
import { TicketCard } from "@/components/TicketCard";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AttendanceActions, SignedDocs, TransferDialog } from "@/components/Attendance";
import { MEALS } from "@/components/AttendeeFields";
import { IndicatorNotice } from "@/components/PriceCards";
import { ReceiptExtras, TicketPdf } from "@/pages/LaunchPages";

const Countdown = ({ to }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const d = Math.max(0, new Date(to) - now);
  const parts = [["Days", Math.floor(d / 864e5)], ["Hrs", Math.floor(d / 36e5) % 24], ["Min", Math.floor(d / 6e4) % 60], ["Sec", Math.floor(d / 1e3) % 60]];
  return (
    <div className="flex gap-3" data-testid="launch-countdown">
      {parts.map(([l, v]) => (
        <div key={l} className="min-w-[64px] rounded-md border border-white/15 bg-[#0B1B33] px-3 py-2 text-center">
          <div className="font-mono-num text-3xl font-semibold text-[#E0A21B]">{String(v).padStart(2, "0")}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/60">{l}</div>
        </div>
      ))}
    </div>
  );
};

export function StudentDashboard() {
  const { user } = useAuth();
  const { data: d, loading } = useFetch("/me/dashboard");
  if (loading) return <Loader />;
  const e = d.next?.event;
  return (
    <>
      <PageTitle title={`Welcome, ${user.name.split(" ")[0]}`} sub="Your mission status at a glance." />
      <div className="relative mb-4 overflow-hidden rounded-lg bg-[#0B1B33] p-6 text-white noise" data-testid="next-launch-card">
        <img src="/brand/hero_art.jpg" alt="" className="absolute right-0 top-0 hidden h-full w-1/2 object-cover opacity-40 md:block" />
        <div className="relative">
          <div className="eyebrow">Next launch</div>
          {e ? (
            <>
              <h2 className="mt-2 font-display text-3xl font-bold uppercase">{e.title}</h2>
              <p className="mt-1 text-sm text-white/70">{fmtDate(e.start_at, e.city?.timezone, { weekday: "long" })} · {e.venue?.name || "Venue being confirmed"}</p>
              <div className="mt-5"><Countdown to={e.start_at} /></div>
              <Link to="/app/tickets" className="btn-gold mt-5" data-testid="dash-view-ticket-btn">View ticket <ArrowRight /></Link>
            </>
          ) : (
            <>
              <h2 className="mt-2 font-display text-3xl font-bold uppercase">No class booked yet</h2>
              <Link to="/cities" className="btn-gold mt-4" data-testid="dash-find-class-btn">Find a class in your city</Link>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Tickets" value={d.tickets} testid="stat-tickets" />
        <Stat label="Quizzes passed" value={`${d.quizzes_passed}/${d.quizzes_total}`} accent="#1E8E5A" testid="stat-quizzes" />
        <Stat label="Badges" value={`${d.badges}/${d.badges_total}`} testid="stat-badges" />
        <Stat label="Unread messages" value={d.unread} accent="#C8382E" testid="stat-unread" />
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <div className="panel p-5 md:col-span-2">
          <div className="flex items-center justify-between"><h3 className="font-display text-xl font-bold uppercase">Flight progress</h3><Link to="/app/quizzes" className="text-sm font-semibold underline">Quizzes</Link></div>
          <div className="mt-4 space-y-4">
            <div><div className="mb-1 flex justify-between text-sm"><span>Quizzes</span><span className="font-mono-num">{Math.round((100 * d.quizzes_passed) / Math.max(1, d.quizzes_total))}%</span></div><Progress value={(100 * d.quizzes_passed) / Math.max(1, d.quizzes_total)} /></div>
            <div><div className="mb-1 flex justify-between text-sm"><span>Badges</span><span className="font-mono-num">{Math.round((100 * d.badges) / Math.max(1, d.badges_total))}%</span></div><Progress value={(100 * d.badges) / Math.max(1, d.badges_total)} /></div>
          </div>
        </div>
        <div className="panel space-y-2 p-5">
          <h3 className="font-display text-xl font-bold uppercase">Next steps</h3>
          {[["/app/referrals", "Bring a friend", Gift], ["/app/quizzes", "Take Launch Prep quiz", Exam], ["/app/badges", "See your badges", Trophy], ["/app/tickets", "Your tickets", Ticket], ["/app/notifications", "Messages", Bell]].map(([to, l, I]) => (
            <Link key={to} to={to} data-testid={`dash-step-${to.split("/").pop()}`} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-[#F4F6FA]"><I size={18} className="text-[#E0A21B]" />{l}</Link>
          ))}
        </div>
      </div>
    </>
  );
}

const EditReg = ({ r, onClose, onSaved }) => {
  const [f, setF] = useState({ attendee_name: r.attendee_name, badge_name: r.badge_name || "", meal: MEALS.includes(r.meal) ? r.meal : "", allergies: r.allergies || "", allergy_severity: r.allergy_severity || "", accessibility: r.accessibility || "" });
  const save = async () => {
    try { await api.put(`/me/registrations/${r.id}`, f); toast.success("Ticket updated"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-registration-dialog">
        <DialogHeader><DialogTitle>Edit ticket {r.ticket_code}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm">Attendee name (transfer)<Input value={f.attendee_name} onChange={(e) => setF({ ...f, attendee_name: e.target.value })} data-testid="edit-reg-name-input" /></label>
          <label className="block text-sm">Name on badge<Input value={f.badge_name} onChange={(e) => setF({ ...f, badge_name: e.target.value })} data-testid="edit-reg-badge-input" /></label>
          <label className="block text-sm">Meal choice<select value={f.meal} onChange={(e) => setF({ ...f, meal: e.target.value })} className="mt-1 h-10 w-full rounded-md border px-3" data-testid="edit-reg-meal-select"><option value="">Not chosen yet</option>{MEALS.map((m) => <option key={m}>{m}</option>)}</select></label>
          <label className="block text-sm">Allergy severity<select value={f.allergy_severity} onChange={(e) => setF({ ...f, allergy_severity: e.target.value })} className="mt-1 h-10 w-full rounded-md border px-3" data-testid="edit-reg-severity-select"><option value="">No allergies</option><option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option></select></label>
          <label className="block text-sm">Accessibility needs<Input value={f.accessibility} onChange={(e) => setF({ ...f, accessibility: e.target.value })} data-testid="edit-reg-access-input" /></label>
          <p className="text-xs text-[#475569]">You can change these details until 7 days before the event.</p>
          <label className="block text-sm">Allergies<Input value={f.allergies} onChange={(e) => setF({ ...f, allergies: e.target.value })} data-testid="edit-reg-allergies-input" /></label>
          <button className="btn-gold w-full" onClick={save} data-testid="edit-reg-save-btn">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CancelReg = ({ r, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const send = async () => {
    try { const { data } = await api.post(`/me/registrations/${r.id}/cancel-request`, { reason }); toast.success(`Request sent. Ticket #${data.ticket_number}`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="cancel-registration-dialog">
        <DialogHeader><DialogTitle>Request cancellation</DialogTitle></DialogHeader>
        <p className="text-sm text-[#475569]">Our support team will review this under the Refund Policy and reply by email.</p>
        <Textarea placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-input" />
        <button className="btn-navy" onClick={send} data-testid="cancel-submit-btn">Send request</button>
      </DialogContent>
    </Dialog>
  );
};

export function Registrations() {
  const { data, loading, reload } = useFetch("/me/registrations");
  const [edit, setEdit] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [transfer, setTransfer] = useState(null);
  if (loading && !data) return <Loader />;
  const upcoming = data.filter((r) => r.event && r.event.end_at >= new Date().toISOString());
  const past = data.filter((r) => !upcoming.includes(r));
  const Row = ({ r }) => (
    <div className="panel flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`registration-row-${r.id}`}>
      <div>
        <div className="font-semibold">{r.event?.title}</div>
        <div className="text-sm text-[#475569]">{r.attendee_name} · {r.tier_name} · {fmtDate(r.event?.start_at, r.event?.city?.timezone)}</div>
        {r.cancel_requested && <div className="mt-1 text-xs font-semibold text-[#C8382E]">Cancellation requested</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <AttendanceActions r={r} reload={reload} onTransfer={setTransfer} />
        {["confirmed", "checked_in"].includes(r.status) && <TicketPdf rid={r.id} />}
        <span className="rounded-full bg-[#F4F6FA] px-2.5 py-1 text-xs font-bold uppercase">{r.status.replace("_", " ")}</span>
        {r.status === "confirmed" && <button className="btn-outline px-3 py-1.5 text-sm" onClick={() => setEdit(r)} data-testid={`edit-reg-btn-${r.id}`}>Edit name / meal</button>}
        {r.status === "confirmed" && !r.cancel_requested && <button className="btn-outline px-3 py-1.5 text-sm" onClick={() => setCancel(r)} data-testid={`cancel-reg-btn-${r.id}`}>Cancel</button>}
      </div>
    </div>
  );
  return (
    <>
      <PageTitle title="My registrations" />
      {data.length === 0 ? <Empty>No registrations yet. <Link to="/cities" className="underline">Find a class</Link>.</Empty> : (
        <div className="space-y-6">
          <div className="space-y-2"><div className="eyebrow">Upcoming</div>{upcoming.map((r) => <Row key={r.id} r={r} />)}</div>
          {past.length > 0 && <div className="space-y-2"><div className="eyebrow">Past</div>{past.map((r) => <Row key={r.id} r={r} />)}</div>}
        </div>
      )}
      {edit && <EditReg r={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
      {cancel && <CancelReg r={cancel} onClose={() => setCancel(null)} onDone={() => { setCancel(null); reload(); }} />}
      {transfer && <TransferDialog r={transfer} onClose={() => setTransfer(null)} onDone={() => { setTransfer(null); reload(); }} />}
    </>
  );
}

export function Tickets() {
  const { data, loading } = useFetch("/me/registrations");
  if (loading) return <Loader />;
  const rows = data.filter((r) => r.status !== "cancelled");
  return (
    <>
      <PageTitle title="Tickets" sub="Show the QR code at the door. Print or save as PDF.">
        <button className="btn-navy no-print" onClick={() => window.print()} data-testid="print-tickets-btn"><Printer /> Print / PDF</button>
      </PageTitle>
      {rows.length === 0 ? <Empty>No tickets yet.</Empty> : <div className="space-y-4">{rows.map((r) => <TicketCard key={r.id} r={r} />)}</div>}
    </>
  );
}

export function Receipts() {
  const { data, loading } = useFetch("/me/orders");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Receipts and invoices" />
      {data.length === 0 ? <Empty>No payments yet.</Empty> : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6FA] text-left text-xs uppercase tracking-wider text-[#475569]"><tr><th className="p-3">Order</th><th className="p-3">Event</th><th className="p-3">Date</th><th className="p-3 text-right">Total</th><th className="p-3">Status</th><th className="p-3" /></tr></thead>
            <tbody>
              {data.map((o) => (
                <tr key={o.id} className="border-t" data-testid={`receipt-row-${o.id}`}>
                  <td className="p-3 font-mono-num">{o.number}</td><td className="p-3">{o.event_title}</td><td className="p-3">{fmtDate(o.created_at)}</td>
                  <td className="p-3 text-right font-mono-num">{money(o.total_cents)}</td><td className="p-3 uppercase">{o.status.replace("_", " ")}</td>
                  <td className="p-3"><Link to={`/app/receipts/${o.id}`} className="font-semibold underline" data-testid={`receipt-view-${o.id}`}>View</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function ReceiptDetail() {
  const { id } = useParams();
  const { data: o, loading, reload } = useFetch(`/me/orders/${id}`);
  const { data: win, reload: reloadWin } = useFetch(`/me/orders/${id}/refund-window`);
  const { data: co } = useFetch("/company");
  if (loading) return <Loader />;
  const selfRefund = async () => {
    if (!window.confirm("Cancel this order and return the credit to your account?")) return;
    try { await api.post(`/me/orders/${id}/refund`); toast.success("Order cancelled. Credit returned."); reload(); reloadWin(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageTitle title="Receipt"><button className="btn-navy no-print" onClick={() => window.print()} data-testid="print-receipt-btn"><Printer /> Print / PDF</button></PageTitle>
      <SignedDocs orderId={id} />
      <ReceiptExtras orderId={id} />
      {win?.credit_only && o.status === "paid" && (
        <div className="panel no-print mb-4 flex flex-wrap items-center justify-between gap-3 p-4 text-sm" data-testid="self-refund-panel">
          {win.open ? <span>Paid fully with account credit. You can cancel and get the credit back until {fmtDate(win.closes_at)}.</span>
            : <span className="text-[#C8382E]" data-testid="self-refund-blocked">The refund window closed on {fmtDate(win.closes_at)}. Contact support if you need help.</span>}
          {win.open && <button className="btn-outline" onClick={selfRefund} data-testid="self-refund-btn">Cancel and return credit</button>}
        </div>
      )}
      <div className="print-area panel p-8" data-testid="receipt-doc">
        <div className="flex flex-wrap justify-between gap-4 border-b pb-6">
          <div><div className="font-display text-2xl font-extrabold uppercase">Mission Control <span className="text-[#E0A21B]">Trading</span></div><div className="text-xs text-[#475569]">ForeXperts AI Inc. · {co?.address || "[Business address]"} · GST/HST No. {co?.gst_hst_number || "[To be provided]"}</div></div>
          <div className="text-right text-sm"><div className="font-mono-num font-semibold">{o.number}</div><div>{fmtDateTime(o.paid_at || o.created_at)}</div><div className="uppercase">{o.status.replace("_", " ")}</div></div>
        </div>
        <div className="mt-6 text-sm"><div className="font-semibold">Billed to</div>{o.user_name}<br />{o.user_email}</div>
        <table className="mt-6 w-full text-sm">
          <tbody>
            <tr className="border-b"><td className="py-2">{o.event_title} – {o.tier_name} × {o.quantity}</td><td className="py-2 text-right font-mono-num">{money(o.subtotal_cents)}</td></tr>
            {o.discount_cents > 0 && <tr className="border-b"><td className="py-2">Discount ({o.coupon_code})</td><td className="py-2 text-right font-mono-num">−{money(o.discount_cents)}</td></tr>}
            {o.taxes.map((t) => <tr key={t.label} className="border-b"><td className="py-2">{t.label} ({t.rate_bp / 100}%)</td><td className="py-2 text-right font-mono-num">{money(t.amount_cents)}</td></tr>)}
            {o.credit_applied_cents > 0 && <tr className="border-b"><td className="py-2">Paid with account credit</td><td className="py-2 text-right font-mono-num">−{money(o.credit_applied_cents)}</td></tr>}
            <tr><td className="py-3 font-bold">Total paid by card ({o.currency === "cad" ? "CA$" : "US$"})</td><td className="py-3 text-right font-mono-num font-bold">{o.currency === "cad" ? money(o.total_cents).replace("US$", "CA$") : money(o.total_cents)}</td></tr>
            {o.refunded_cents > 0 && <tr><td className="py-1 text-[#C8382E]">Refunded</td><td className="py-1 text-right font-mono-num text-[#C8382E]">−{money(o.refunded_cents)}</td></tr>}
          </tbody>
        </table>
        <p className="mt-4 text-sm font-semibold" data-testid="receipt-currency-line">{o.currency === "cad" ? "Amounts in Canadian dollars" : "Amounts in US dollars"}</p>
        {o.fx_rate_usdcad && <p className="text-xs text-[#475569]" data-testid="receipt-fx">For tax records: Bank of Canada USD/CAD {o.fx_rate_usdcad} ({o.fx_rate_date}). Total CA${(o.total_cad_cents / 100).toFixed(2)}, tax CA${(o.tax_cad_cents / 100).toFixed(2)}.</p>}
        <IndicatorNotice testid="indicator-notice-receipt" className="mt-4" />
        <div className="mt-6 text-sm"><div className="font-semibold">Attendees</div>{o.registrations.map((r) => <div key={r.id}>{r.attendee_name} · <span className="font-mono-num">{r.ticket_code}</span></div>)}</div>
        <p className="mt-8 border-t pt-4 text-xs text-[#475569]">{RISK}</p>
      </div>
    </>
  );
}
